import classNames from "classnames";
import React, { RefAttributes, useMemo } from "react";
import { HTMLAttributes } from "react";
import useElementHover from "../../hooks/useElementHover";
import { Transition } from "@headlessui/react";
import { WithChildren } from "../../helpers/constants/types";

export type CardProps = { shadow?: boolean } & HTMLAttributes<HTMLDivElement>;
type HoverOverlayProps = WithChildren;

interface CardComponent
  extends React.ForwardRefExoticComponent<
    HTMLAttributes<HTMLDivElement> & RefAttributes<HTMLDivElement>
  > {
  HoverOverlay: React.FC<HoverOverlayProps>;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ children, className, shadow = true, ...props }: CardProps, ref) => {
    const { hover, elementRef } = useElementHover();

    const setRefs = (node: HTMLDivElement | null) => {
      if (ref) {
        if (typeof ref === "function") {
          ref(node);
        } else if (typeof ref === "object" && ref !== null) {
          ref.current = node;
        }
      }
      elementRef.current = node;
    };

    const overlayElement = useMemo(
      () =>
        React.Children.toArray(children).filter(
          (child) => React.isValidElement(child) && child.type === HoverOverlay,
        )[0],
      [children],
    );

    return (
      <div
        ref={setRefs}
        className={classNames(
          "relative flex items-center rounded-lg bg-white p-4",
          shadow && "shadow-sm",
          className,
        )}
        {...props}
      >
        {React.Children.toArray(children).filter(
          (child) => React.isValidElement(child) && child.type !== HoverOverlay,
        )}
        {overlayElement ? (
          <Transition
            show={hover}
            enter="transition-opacity duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            {overlayElement}
          </Transition>
        ) : null}
      </div>
    );
  },
) as CardComponent;

const HoverOverlay: React.FC<HoverOverlayProps> = ({ children }) => {
  return (
    <div className="absolute inset-0 flex items-center justify-center space-x-4 rounded-md bg-black bg-opacity-30">
      {children}
    </div>
  );
};

Card.HoverOverlay = HoverOverlay;

export default Card;
